import React, { useState, useEffect } from "react";
import { Card, Row, Col, Modal, Table, Badge } from "react-bootstrap";
import { getApiCall } from "../../../service/httpCall";
import { API_USERS_PROFILE } from "../../../service/url";
import { useAuth, errorCheck } from "../auth/auth-utils";
import { getUser } from "../../../utilities/setting";
import { LICENCE_LABEL, USER_ROLES_MAP, USER_ROLES } from "../../../utilities/constant";
import Loader from "../../../components/partials/components/loader";
import { toast } from 'react-toastify';
import { 
  FiInfo, FiUser, FiMail, FiShield, FiCalendar, FiCreditCard, 
  FiBriefcase, FiUsers, FiMapPin, FiLayers, FiClock, FiCheckCircle, FiXCircle,
  FiHome, FiPhone, FiMap, FiKey, FiActivity
} from "react-icons/fi";

const UserProfilePage = () => {
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showACLModal, setShowACLModal] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showLocationsModal, setShowLocationsModal] = useState(false);
  const [showCallCenterModal, setShowCallCenterModal] = useState(false);
  const [showVoicePlanModal, setShowVoicePlanModal] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const user = await getUser();
      if (!user || !user.id) {
        toast.error("User not found");
        setLoading(false);
        return;
      }

      // Try to fetch from API, but fallback to local data if it fails
      try {
        const profileApiUrl = API_USERS_PROFILE.replace(":id", user.id);
        const resp = await getApiCall(profileApiUrl, {});
        setUserData(resp);
      } catch (apiError) {
        // If API fails (e.g., due to database schema issues), fall back to basic user data from storage
        console.warn("API call failed, using basic user data from storage:", apiError);
        
        // Use basic user data from AsyncStorage as fallback
        const basicUserData = {
          username: user.username || "N/A",
          name: user.name || "N/A",
          email: user.email || "N/A",
          role: user.role,
          status: user.status,
          expiry_date: user.expiry_date,
          credits_available: user.credits_available,
          tts_credits: user.tts_credits,
          company: user.company,
          assignedCompany: user.assignedCompany,
        };
        
        setUserData(basicUserData);
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
      // Even if getUser fails, try to show something
      try {
        const user = await getUser();
        if (user) {
          const basicUserData = {
            username: user.username || "N/A",
            name: user.name || "N/A",
            email: user.email || "N/A",
            role: user.role,
            status: user.status,
            expiry_date: user.expiry_date,
            credits_available: user.credits_available,
            tts_credits: user.tts_credits,
            company: user.company,
            assignedCompany: user.assignedCompany,
          };
          setUserData(basicUserData);
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
        toast.error("Failed to load user profile");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!userData) {
    return (
      <div className="py-4 px-4">
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <h3 className="mb-3">User Profile</h3>
            <hr className="mt-2 mb-4" />
            <p className="text-center text-muted">Unable to load user profile data.</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Determine role label based on logged-in user type
  const getRoleLabel = () => {
    const role = userData.role;
    if (role === USER_ROLES.COMPANY_ADMIN) return "Company";
    if (role === USER_ROLES.CALL_CENTRE_ADMIN || role === USER_ROLES.CALL_CENTRE_TEAM_LEAD) return "Call Centre";
    if (role === USER_ROLES.CALL_CENTRE_AGENT) return "Agent";
    return USER_ROLES_MAP[role] || role;
  };
  const userRoleLabel = getRoleLabel();
  const expiryDate = userData.expiry_date ? new Date(userData.expiry_date).toLocaleDateString() : "N/A";
  const joinedDate = userData.joined_date || userData.created_at ? 
    new Date(userData.joined_date || userData.created_at).toLocaleDateString() : "N/A";
  const companyData = userData.assigned_company_details || userData.assignedCompany || userData.company;
  const callCenterData = userData.assigned_call_center_details || userData.assignedCallCenter;
  const aclData = userData.acl_details || userData.acl_settings;
  const companyGroups = userData.company_groups || [];
  const companyLocations = userData.company_locations || [];
  const userGroups = userData.user_groups || [];
  const userLocations = userData.user_locations || [];
  const voicePlanNumbers = userData.number_voice_plans || [];
  const showAvailableLicenceSection = userData.role !== USER_ROLES.SUPERADMIN && userData.role !== USER_ROLES.CALL_CENTRE_AGENT;

  const getLicenceFromEntity = (entity) => {
    if (!entity) return null;
    return (
      entity.sub_user_licence ||
      entity.subUserLicenceId ||
      entity.sub_user_licence_id ||
      null
    );
  };

  const renderLicenceTable = (licence) => {
    const available = licence?.available_licence || {};
    const used = licence?.used_licence || {};

    if (typeof available !== "object" || typeof used !== "object") {
      return (
        <div className="text-center py-3 text-muted">
          No licence information available.
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th style={{ width: "50%" }}>Type</th>
              <th style={{ width: "25%" }}>Available</th>
              <th style={{ width: "25%" }}>Used</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(LICENCE_LABEL).map((key) => (
              <tr key={key}>
                <td>{LICENCE_LABEL[key] || key}</td>
                <td className="text-center fw-bold">{available?.[key] ?? 0}</td>
                <td className="text-center">{used?.[key] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="py-4 profile-page-shell">
      <style>{`
        @media (min-width: 768px) {
          .profile-right-col {
            border-left: 1px solid #dee2e6;
          }
        }
      `}</style>
      <Row>
        <Col xs={12} style={{ overflow: "visible" }}>
          <Card className="border-0" style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)", overflow: "visible", width: "100%" }}>
            <Card.Header 
              className="text-white p-4" 
              style={{ background: "linear-gradient(45deg, #08063d, rgb(7,64,141))" }}
            >
              <h3 className="mb-0 text-white">User Profile</h3>
            </Card.Header>
            <Card.Body className="p-4" style={{ overflow: "visible" }}>
              <Row className="gx-4">
                {/* Left Column */}
                {/* <Col xs={12} md={6} className="pe-md-4 mb-4 mb-md-0" style={{ minWidth: 0, maxWidth: "100%" }}> */}
                  <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                    <Table borderless className="mb-0" style={{ fontSize: "0.9rem", width: "100%", tableLayout: "auto", maxWidth: "100%" }}>
                    <tbody>
                      <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                        <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                          <div className="d-flex align-items-start">
                            <FiUser className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                            <span>Username:</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>{userData.username || "N/A"}</td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                        <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                          <div className="d-flex align-items-start">
                            <FiUser className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                            <span>Name:</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>{userData.name || "N/A"}</td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                        <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                          <div className="d-flex align-items-start">
                            <FiMail className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                            <span>Email:</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-all", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>{userData.email || "N/A"}</td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                        <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                          <div className="d-flex align-items-start">
                            <FiShield className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                            <span>Role:</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>{userRoleLabel}</td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                        <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                          <div className="d-flex align-items-start">
                            <FiCalendar className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                            <span>Expiry Date:</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>{expiryDate}</td>
                      </tr>
                      {/* {userData.credits_available !== undefined && (
                        <tr>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "6px 0", verticalAlign: "middle", fontSize: "0.9rem" }}>
                            <div className="d-flex align-items-center">
                              <span className="me-2 d-flex align-items-center justify-content-center" style={{ minWidth: "22px", fontSize: "18px", fontWeight: "600", lineHeight: "1" }}>₹</span>
                              <span>Credits Available:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }} className="text-success fw-bold">{userData.credits_available || "0.00"}</td>
                        </tr>
                      )}
                      {userData.tts_credits !== undefined && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <span className="me-2 d-flex align-items-center justify-content-center" style={{ minWidth: "22px", fontSize: "18px", fontWeight: "600", lineHeight: "1" }}>₹</span>
                              <span>TTS Credits:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }} className="text-info fw-bold">{userData.tts_credits || "0.00"}</td>
                        </tr>
                      )} */}
                      <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                        <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                          <div className="d-flex align-items-start">
                            <FiClock className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                            <span>Joined Date:</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>{joinedDate}</td>
                      </tr>
                      {userData.parent_username && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiUsers className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Parent Username:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            {userData.parent_username}
                            {userData.parent_name && ` (${userData.parent_name})`}
                          </td>
                        </tr>
                      )}
                      {userData.parent_parent_username && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiUsers className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Parent's Parent:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            {userData.parent_parent_username}
                            {userData.parent_parent_name && ` (${userData.parent_parent_name})`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  </div>
                {/* </Col> */}

                {/* Right Column */}
                {/* <Col xs={12} md={6} className="ps-md-4 mt-4 mt-md-0 profile-right-col" style={{ minWidth: 0, maxWidth: "100%" }}> */}
                  <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                    <Table borderless className="mb-0" style={{ fontSize: "0.9rem", width: "100%", tableLayout: "auto", maxWidth: "100%" }}>
                    <tbody>
                      {companyData && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiBriefcase className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Assigned Company:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            <div className="d-flex align-items-start flex-wrap" style={{ gap: "8px" }}>
                              <button
                                onClick={() => setShowCompanyModal(true)}
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  border: "none",
                                  background: "none",
                                  color: "#007bff",
                                  padding: "0.2rem 0.4rem",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  transition: "all 0.2s",
                                  fontSize: "0.85rem",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.textDecoration = "underline";
                                  e.target.style.backgroundColor = "rgba(0, 123, 255, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.textDecoration = "none";
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FiInfo className="me-1" size={14} />
                                View Details
                              </button>
                              <span style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{companyData.name || "N/A"}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {callCenterData && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiHome className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Assigned Call Center:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            <div className="d-flex align-items-start flex-wrap" style={{ gap: "8px" }}>
                              <button
                                onClick={() => setShowCallCenterModal(true)}
                                className="btn btn-sm btn-outline-info"
                                style={{
                                  border: "none",
                                  background: "none",
                                  color: "#17a2b8",
                                  padding: "0.2rem 0.4rem",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  transition: "all 0.2s",
                                  fontSize: "0.85rem",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.textDecoration = "underline";
                                  e.target.style.backgroundColor = "rgba(23, 162, 184, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.textDecoration = "none";
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FiInfo className="me-1" size={14} />
                                View Details
                              </button>
                              <span style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{callCenterData.name || "N/A"}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Show Flow Type for Call Center Admin */}
                      {callCenterData && callCenterData.flow_type !== undefined && callCenterData.flow_type !== null && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiInfo className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Flow Type:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            <Badge bg={callCenterData.flow_type === 1 ? "primary" : "secondary"} style={{ fontSize: "0.85rem" }}>
                              {callCenterData.flow_type === 1 ? "Jambonz" : "Asterisk"}
                            </Badge>
                          </td>
                        </tr>
                      )}
                      {/* Show Agent Details for Agents */}
                      {userData.agent_details && (
                        <>
                          {userData.agent_details.extension_name && (
                            <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                              <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                                <div className="d-flex align-items-start">
                                  <FiUser className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                                  <span>Extension Name:</span>
                                </div>
                              </td>
                              <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                                {userData.agent_details.extension_name}
                              </td>
                            </tr>
                          )}
                          {userData.agent_details.access && (
                            <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                              <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                                <div className="d-flex align-items-start">
                                  <FiShield className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                                  <span>Access:</span>
                                </div>
                              </td>
                              <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                                <Badge bg="info" className="profile-access-badge" style={{ fontSize: "0.85rem" }}>
                                  {userData.agent_details.access}
                                </Badge>
                              </td>
                            </tr>
                          )}
                          {userData.agent_details.type && Array.isArray(userData.agent_details.type) && userData.agent_details.type.length > 0 && (
                            <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                              <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                                <div className="d-flex align-items-start">
                                  <FiInfo className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                                  <span>Type:</span>
                                </div>
                              </td>
                              <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                                <div className="d-flex flex-wrap gap-2">
                                  {userData.agent_details.type.map((type, idx) => (
                                    <Badge key={idx} bg="warning" text="dark" className="profile-agent-type-badge" style={{ fontSize: "0.85rem" }}>
                                      {type}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                      {aclData && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiKey className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Assigned ACL:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            <div className="d-flex align-items-start flex-wrap" style={{ gap: "8px" }}>
                              <button
                                onClick={() => setShowACLModal(true)}
                                className="btn btn-sm btn-outline-info"
                                style={{
                                  border: "none",
                                  background: "none",
                                  color: "#17a2b8",
                                  padding: "0.2rem 0.4rem",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  transition: "all 0.2s",
                                  fontSize: "0.85rem",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.textDecoration = "underline";
                                  e.target.style.backgroundColor = "rgba(23, 162, 184, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.textDecoration = "none";
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FiInfo className="me-1" size={14} />
                                View Details
                              </button>
                              <span style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{aclData.acl_name || "N/A"}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {voicePlanNumbers.length > 0 && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiPhone className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Voice Plans:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", overflow: "hidden" }}>
                            <div className="d-flex align-items-start flex-wrap" style={{ gap: "8px" }}>
                              <button
                                onClick={() => setShowVoicePlanModal(true)}
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  border: "none",
                                  background: "none",
                                  color: "#007bff",
                                  padding: "0.2rem 0.4rem",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  transition: "all 0.2s",
                                  fontSize: "0.85rem",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.textDecoration = "underline";
                                  e.target.style.backgroundColor = "rgba(0, 123, 255, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.textDecoration = "none";
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FiInfo className="me-1" size={14} />
                                View Details
                              </button>
                              <span>{voicePlanNumbers.length} Number(s) linked</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {(companyGroups.length > 0 || userGroups.length > 0) && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiLayers className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Groups:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word" }}>
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                              <button
                                onClick={() => setShowGroupsModal(true)}
                                className="btn btn-sm btn-outline-success"
                                style={{
                                  border: "none",
                                  background: "none",
                                  color: "#28a745",
                                  padding: "0.2rem 0.4rem",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  fontSize: "0.85rem",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                <FiInfo className="me-1" size={14} />
                                View All ({companyGroups.length + userGroups.length})
                              </button>
                              {companyGroups.slice(0, 3).map((group, idx) => (
                                <Badge key={idx} bg="success" className="mb-1" style={{ fontSize: "0.8rem" }}>
                                  {group.group_name || `Group ${group.group_id}`}
                                </Badge>
                              ))}
                              {companyGroups.length > 3 && (
                                <Badge bg="secondary" className="mb-1" style={{ fontSize: "0.8rem" }}>
                                  +{companyGroups.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {(companyLocations.length > 0 || userLocations.length > 0) && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiMapPin className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Locations:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word" }}>
                            <button
                              onClick={() => setShowLocationsModal(true)}
                              className="btn btn-sm btn-outline-warning"
                              style={{
                                border: "none",
                                background: "none",
                                color: "#ffc107",
                                padding: "0.2rem 0.4rem",
                                cursor: "pointer",
                                borderRadius: "4px",
                                fontSize: "0.85rem"
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.textDecoration = "underline";
                                e.target.style.backgroundColor = "rgba(255, 193, 7, 0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.textDecoration = "none";
                                e.target.style.backgroundColor = "transparent";
                              }}
                            >
                              <FiInfo className="me-1" size={14} />
                              View All ({companyLocations.length + userLocations.length})
                            </button>
                          </td>
                        </tr>
                      )}
                      {userData.account_expiry_date && (
                        <tr style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.05)" }}>
                          <td style={{ fontWeight: "600", color: "#495057", padding: "8px 0 8px 16px", verticalAlign: "top", fontSize: "0.9rem", whiteSpace: "nowrap", width: "35%" }}>
                            <div className="d-flex align-items-start">
                              <FiActivity className="me-2" size={18} style={{ minWidth: "22px", flexShrink: 0, marginTop: "2px" }} />
                              <span>Account Status:</span>
                            </div>
                          </td>
                          <td style={{ padding: "8px 0", verticalAlign: "top", paddingLeft: "32px", fontSize: "0.9rem", wordBreak: "break-word" }}>
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                              {(() => {
                                // Expired only if date has passed (not today)
                                const expiryDate = new Date(userData.account_expiry_date);
                                const today = new Date();
                                expiryDate.setHours(0, 0, 0, 0);
                                today.setHours(0, 0, 0, 0);
                                const isExpired = expiryDate < today;
                                const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                                
                                return (
                                  <>
                                    {isExpired ? (
                                      <Badge bg="danger" style={{ fontSize: "0.85rem" }}>
                                        <FiXCircle className="me-1" /> Expired
                                      </Badge>
                                    ) : (
                                      <Badge bg="success" style={{ fontSize: "0.85rem" }}>
                                        <FiCheckCircle className="me-1" /> Active
                                      </Badge>
                                    )}
                                    {daysUntilExpiry !== undefined && (
                                      <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                        {daysUntilExpiry > 0 
                                          ? `${daysUntilExpiry} days remaining`
                                          : daysUntilExpiry === 0
                                          ? "Expires today"
                                          : `${Math.abs(daysUntilExpiry)} days expired`}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  </div>
                {/* </Col> */}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Company Details Modal */}
      {companyData && (
        <Modal show={showCompanyModal} onHide={() => setShowCompanyModal(false)} centered size="lg">
          <Modal.Header 
            closeButton 
            className="text-white"
            style={{ background: "linear-gradient(45deg, #08063d, rgb(7,64,141))" }}
          >
            <Modal.Title className="text-white">Company Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {showAvailableLicenceSection && (
              <div className="mb-4">
                <h6 className="mb-3" style={{ fontWeight: 700 }}>
                  Licence
                </h6>
                {renderLicenceTable(getLicenceFromEntity(companyData))}
              </div>
            )}
            <Table borderless>
              <tbody>
                <tr>
                  <td style={{ width: "30%", fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                    <div className="d-flex align-items-center">
                      <FiBriefcase className="me-3" size={20} style={{ minWidth: "26px" }} />
                      <span>Company Name:</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>{companyData.name || "N/A"}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                    <div className="d-flex align-items-center">
                      <FiPhone className="me-3" size={20} style={{ minWidth: "26px" }} />
                      <span>Company Phone:</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>{companyData.phone || "N/A"}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "top" }}>
                    <div className="d-flex align-items-center">
                      <FiMap className="me-3" size={20} style={{ minWidth: "26px" }} />
                      <span>Company Address:</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 0", verticalAlign: "top", paddingLeft: "32px" }}>{companyData.address || "N/A"}</td>
                </tr>
                {companyData.pincode && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiMapPin className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>Pincode:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>{companyData.pincode}</td>
                  </tr>
                )}
                {companyData.credits_available !== undefined && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <span className="me-3 d-flex align-items-center justify-content-center" style={{ minWidth: "26px", fontSize: "20px", fontWeight: "600", lineHeight: "1" }}>₹</span>
                        <span>Company Voice Credits:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }} className="text-success fw-bold">
                      {companyData.credits_available || "0.00"}
                    </td>
                  </tr>
                )}
                {companyData.tts_credits !== undefined && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <span className="me-3 d-flex align-items-center justify-content-center" style={{ minWidth: "26px", fontSize: "20px", fontWeight: "600", lineHeight: "1" }}>₹</span>
                        <span>Company TTS Credits:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "12px" }} className="text-success fw-bold">
                      {companyData.tts_credits || "0.00"}
                    </td>
                  </tr>
                )}
                {companyData.is_reseller_company !== undefined && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiInfo className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>Reseller Company:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>
                      <Badge bg={companyData.is_reseller_company ? "success" : "secondary"}>
                        {companyData.is_reseller_company ? "Yes" : "No"}
                      </Badge>
                    </td>
                  </tr>
                )}
                {companyData.is_own_company !== undefined && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiInfo className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>End User:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>
                      <Badge bg={companyData.is_own_company ? "primary" : "secondary"}>
                        {companyData.is_own_company ? "Yes" : "No"}
                      </Badge>
                    </td>
                  </tr>
                )}
                {companyData.created_at && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiClock className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>Created At:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>{formatDate(companyData.created_at)}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowCompanyModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* ACL Details Modal */}
      {aclData && (
        <Modal show={showACLModal} onHide={() => setShowACLModal(false)} centered size="lg">
          <Modal.Header 
            closeButton 
            className="text-white"
            style={{ background: "linear-gradient(45deg, #17a2b8, #138496)" }}
          >
            <Modal.Title className="text-white">Your Assigned Permissions</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <FiKey className="me-2" size={18} />
                <strong>ACL Name:</strong>
                <span className="ms-2">{aclData.acl_name || "N/A"}</span>
              </div>
            </div>
            {(() => {
              // Parse module_operations - handle both string and object formats
              let moduleOperations = null;
              
              try {
                if (aclData.module_operations) {
                  if (typeof aclData.module_operations === 'string') {
                    // If it's a string, parse it
                    try {
                      moduleOperations = JSON.parse(aclData.module_operations);
                    } catch (parseError) {
                      console.error("Error parsing module_operations string:", parseError);
                      moduleOperations = null;
                    }
                  } else if (typeof aclData.module_operations === 'object' && aclData.module_operations !== null) {
                    // If it's already an object, use it directly
                    moduleOperations = aclData.module_operations;
                  }
                }
              } catch (error) {
                console.error("Error processing module_operations:", error, "Raw data:", aclData.module_operations);
                moduleOperations = null;
              }

              // Debug logging
              if (moduleOperations) {
                // console.log("Parsed module_operations:", moduleOperations);
                // console.log("Number of modules:", Object.keys(moduleOperations).length);
              }

              if (!moduleOperations || typeof moduleOperations !== 'object' || Object.keys(moduleOperations).length === 0) {
                return (
                  <div className="text-center py-4">
                    <FiInfo className="mb-2" size={32} style={{ color: "#6c757d" }} />
                    <p className="text-muted mb-0">No permissions assigned to this ACL</p>
                    <small className="text-muted d-block mt-2">
                      This ACL does not have any enabled permissions configured.
                    </small>
                  </div>
                );
              }

              // Get all modules with at least one permission enabled
              const enabledModules = Object.entries(moduleOperations)
                .filter(([module, ops]) => {
                  if (!ops || typeof ops !== 'object' || ops === null) {
                    // console.log(`Module ${module} has invalid operations:`, ops);
                    return false;
                  }
                  // Check if any permission is true
                  const hasPermission = Object.values(ops).some(val => val === true);
                  if (!hasPermission) {
                    // console.log(`Module ${module} has no enabled permissions:`, ops);
                  }
                  return hasPermission;
                })
                .map(([module, ops]) => {
                  const permissions = ops && typeof ops === 'object' ? ops : {};
                  const enabledPermissions = [];
                  
                  // Check each permission type (case-insensitive and handle different formats)
                  if (permissions.read === true || permissions.Read === true || permissions.READ === true) {
                    enabledPermissions.push({ type: 'Read', color: 'success' });
                  }
                  if (permissions.add === true || permissions.Add === true || permissions.ADD === true) {
                    enabledPermissions.push({ type: 'Add', color: 'primary' });
                  }
                  if (permissions.edit === true || permissions.Edit === true || permissions.EDIT === true) {
                    enabledPermissions.push({ type: 'Edit', color: 'warning' });
                  }
                  if (permissions.delete === true || permissions.Delete === true || permissions.DELETE === true) {
                    enabledPermissions.push({ type: 'Delete', color: 'danger' });
                  }
                  if (permissions.view_permission === true || permissions.View_Permission === true || permissions.VIEW_PERMISSION === true || permissions.viewPermission === true || permissions.ViewPermission === true) {
                    enabledPermissions.push({ type: 'View Permission', color: 'info' });
                  }
                  
                  // console.log(`Module ${module} has ${enabledPermissions.length} enabled permissions:`, enabledPermissions);
                  return { module, permissions: enabledPermissions };
                });

              return enabledModules.length > 0 ? (
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <FiLayers className="me-2" size={18} />
                    <strong>Your Assigned Permissions:</strong>
                    <Badge bg="info" className="ms-2">
                      {enabledModules.length} Module{enabledModules.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: "450px", overflowY: "auto" }}>
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 10 }}>
                        <tr>
                          <th style={{ width: "35%", fontWeight: "600" }}>Module</th>
                          <th style={{ width: "65%", fontWeight: "600" }}>Assigned Permissions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enabledModules.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: "500", verticalAlign: "middle" }}>
                              <div className="d-flex align-items-center">
                                <FiLayers className="me-2 text-info" size={16} />
                                <span>{item.module}</span>
                              </div>
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              <div className="d-flex flex-wrap gap-2">
                                {item.permissions.map((perm, permIdx) => (
                                  <Badge 
                                    key={permIdx} 
                                    bg={perm.color}
                                    className="d-inline-flex align-items-center"
                                    style={{ fontSize: "0.85rem", padding: "0.35rem 0.65rem" }}
                                  >
                                    {perm.type === 'Read' && <FiCheckCircle className="me-1" size={12} />}
                                    {perm.type === 'Add' && <FiCheckCircle className="me-1" size={12} />}
                                    {perm.type === 'Edit' && <FiCheckCircle className="me-1" size={12} />}
                                    {perm.type === 'Delete' && <FiCheckCircle className="me-1" size={12} />}
                                    {perm.type === 'View Permission' && <FiCheckCircle className="me-1" size={12} />}
                                    {perm.type}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FiInfo className="mb-2" size={32} style={{ color: "#6c757d" }} />
                  <p className="text-muted mb-0">No permissions assigned to this ACL</p>
                  <small className="text-muted d-block mt-2">
                    This ACL does not have any enabled permissions configured.
                  </small>
                </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowACLModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Call Center Details Modal */}
      {callCenterData && (
        <Modal show={showCallCenterModal} onHide={() => setShowCallCenterModal(false)} centered size="lg">
          <Modal.Header 
            closeButton 
            className="text-white"
            style={{ background: "linear-gradient(45deg, #17a2b8, #138496)" }}
          >
            <Modal.Title className="text-white">Call Center Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {showAvailableLicenceSection && (
              <div className="mb-4">
                <h6 className="mb-3" style={{ fontWeight: 700 }}>
                  Licence
                </h6>
                {renderLicenceTable(getLicenceFromEntity(callCenterData))}
              </div>
            )}
            <Table borderless>
              <tbody>
                <tr>
                  <td style={{ width: "30%", fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                    <div className="d-flex align-items-center">
                      <FiHome className="me-3" size={20} style={{ minWidth: "26px" }} />
                      <span>Call Center Name:</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>{callCenterData.name || "N/A"}</td>
                </tr>
                {callCenterData.domain && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiInfo className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>Domain:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>{callCenterData.domain}</td>
                  </tr>
                )}
                {callCenterData.flow_type !== undefined && callCenterData.flow_type !== null && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiInfo className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>Flow Type:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>
                      <Badge bg={callCenterData.flow_type === 1 ? "primary" : "secondary"} style={{ fontSize: "0.9rem" }}>
                        {callCenterData.flow_type === 1 ? "Jambonz" : "Asterisk"}
                      </Badge>
                    </td>
                  </tr>
                )}
                {callCenterData.created_at && (
                  <tr>
                    <td style={{ fontWeight: "600", color: "#495057", padding: "10px 0", verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center">
                        <FiCalendar className="me-3" size={20} style={{ minWidth: "26px" }} />
                        <span>Created At:</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 0", verticalAlign: "middle", paddingLeft: "32px" }}>
                      {formatDate(callCenterData.created_at)}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowCallCenterModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Voice Plan Details Modal */}
      {voicePlanNumbers.length > 0 && (
        <Modal show={showVoicePlanModal} onHide={() => setShowVoicePlanModal(false)} centered size="xl">
          <Modal.Header
            closeButton
            className="text-white"
            style={{ background: "linear-gradient(45deg, #0d47a1, #1976d2)" }}
          >
            <Modal.Title className="text-white">Numbers with Connected Voice Plans</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Type</th>
                    <th>Voice Plan</th>
                    <th>Plan Details (Duration / Price)</th>
                  </tr>
                </thead>
                <tbody>
                  {voicePlanNumbers.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td>{item.actual_number || "N/A"}</td>
                      <td>{item.number_type || "N/A"}</td>
                      <td>{item.voice_plan?.plan_name || "Not Assigned"}</td>
                      <td>
                        {Array.isArray(item.voice_plan?.plans) && item.voice_plan.plans.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {item.voice_plan.plans.map((plan, pIdx) => (
                              <Badge key={pIdx} bg="info" style={{ fontSize: "0.8rem" }}>
                                {plan.plan_type}: {plan.pulse_duration ?? "N/A"} / {plan.pulse_price ?? "N/A"}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowVoicePlanModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Groups Modal */}
      {(companyGroups.length > 0 || userGroups.length > 0) && (
        <Modal show={showGroupsModal} onHide={() => setShowGroupsModal(false)} centered size="lg">
          <Modal.Header 
            closeButton 
            className="text-white"
            style={{ background: "linear-gradient(45deg, #28a745, #20c997)" }}
          >
            <Modal.Title className="text-white">Groups Information</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {companyGroups.length > 0 && (
              <div className="mb-4">
                <h5 className="mb-3">
                  <FiLayers className="me-2" />
                  Company Groups ({companyGroups.length})
                </h5>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Group Name</th>
                        <th>Group ID</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyGroups.map((group, idx) => (
                        <tr key={idx}>
                          <td>{group.group_name || `Group ${group.group_id}`}</td>
                          <td>{group.group_id}</td>
                          <td>{formatDate(group.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            {userGroups.length > 0 && (
              <div>
                <h5 className="mb-3">
                  <FiUsers className="me-2" />
                  User Groups ({userGroups.length})
                </h5>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Group ID</th>
                        <th>Company ID</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userGroups.map((group, idx) => (
                        <tr key={idx}>
                          <td>{group.group_id}</td>
                          <td>{group.company_id}</td>
                          <td>{formatDate(group.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowGroupsModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Locations Modal */}
      {(companyLocations.length > 0 || userLocations.length > 0) && (
        <Modal show={showLocationsModal} onHide={() => setShowLocationsModal(false)} centered size="lg">
          <Modal.Header 
            closeButton 
            className="text-white"
            style={{ background: "linear-gradient(45deg, #ffc107, #ff9800)" }}
          >
            <Modal.Title className="text-white">Locations Information</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {companyLocations.length > 0 && (
              <div className="mb-4">
                <h5 className="mb-3">
                  <FiMapPin className="me-2" />
                  Company Locations ({companyLocations.length})
                </h5>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Location Name</th>
                        <th>Location ID</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyLocations.map((location, idx) => (
                        <tr key={idx}>
                          <td>{location.location_name || `Location ${location.location_id}`}</td>
                          <td>{location.location_id}</td>
                          <td>{formatDate(location.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            {userLocations.length > 0 && (
              <div>
                <h5 className="mb-3">
                  <FiUsers className="me-2" />
                  User Locations ({userLocations.length})
                </h5>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Location ID</th>
                        <th>Company ID</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userLocations.map((location, idx) => (
                        <tr key={idx}>
                          <td>{location.location_id}</td>
                          <td>{location.company_id}</td>
                          <td>{formatDate(location.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowLocationsModal(false)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default UserProfilePage;

